import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiCalendar, FiMapPin, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { eventAPI } from "../services/api";
import { formatDate, resolveImageUrl } from "../utils/helpers";
import { useDataRefresh } from "../hooks/useDataRefresh";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useSiteSettings } from "../context/SiteSettingsContext";

const CATEGORIES = ["All", "Academic", "Sports", "Cultural", "Meeting", "Holiday", "Other"];

const categoryColors = {
  Academic: "bg-[#2563EB] text-white",
  Sports:   "bg-[#0EA5E9] text-white",
  Cultural: "bg-[#1e40af] text-white",
  Meeting:  "bg-[#1e3a8a] text-white",
  Holiday:  "bg-sky-400 text-white",
  Other:    "bg-slate-500 text-white",
};

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

const NewsEvents = () => {
  const { settings } = useSiteSettings();
  const [filtered, setFiltered] = useState([]);
  const [category, setCategory] = useState("All");
  const [page, setPage] = useState(1);
  const perPage = 6;

  const { data: events = [], loading } = useDataRefresh(
    async () => {
      const response = await eventAPI.getAll();
      return response?.data?.data?.events || [];
    },
    "events",
    { pollInterval: 10000 },
  );

  useEffect(() => {
    const result = category === "All" ? events : events.filter((e) => e.category === category);
    setFiltered(result);
    setPage(1);
  }, [category, events]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Stay Informed</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              News &amp; <span className="text-sky-300">Events</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed">
              Stay updated with the latest happenings at {settings?.schoolName || "Early Boarding School & Montessori"}.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 py-16 lg:py-24">
        {/* Category Filter */}
        <div className="flex items-center gap-2 mb-10 flex-wrap">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider rounded-xl transition-all duration-200 ${
                category === cat
                  ? "bg-[#2563EB] text-white shadow-md shadow-blue-200"
                  : "bg-[#F0F4FF] text-slate-600 hover:bg-blue-100 hover:text-[#2563EB] border border-blue-100"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((event, idx) => (
                <motion.article
                  key={event._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white rounded-2xl border border-blue-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col group"
                >
                  <div className="h-48 bg-[#F0F4FF] relative flex items-center justify-center overflow-hidden">
                    {event.image?.url ? (
                      <img
                        src={resolveImageUrl(event.image.url)}
                        alt={event.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <FiCalendar className="w-12 h-12 text-blue-200" />
                    )}
                    <div className="absolute top-4 left-4 z-10">
                      <span className={`px-3 py-1 text-xs font-bold rounded-lg ${categoryColors[event.category] || categoryColors.Other}`}>
                        {event.category || "Event"}
                      </span>
                    </div>
                    {event.date && (
                      <div className="absolute bottom-0 right-0 z-10 bg-[#2563EB] px-4 py-2 text-center rounded-tl-xl">
                        <div className="text-white font-bold text-xs uppercase tracking-widest">
                          {formatDate(event.date, "MMM dd")}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-bold text-[#0F172A] text-xl mb-3 line-clamp-2 group-hover:text-[#2563EB] transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-5 line-clamp-3 flex-1">
                      {event.description}
                    </p>
                    <div className="space-y-2 pt-4 border-t border-blue-50">
                      {event.date && (
                        <div className="flex items-center gap-2 text-slate-400 text-xs">
                          <FiCalendar className="w-3.5 h-3.5 text-[#2563EB]" />
                          {formatDate(event.date)}
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-2 text-slate-400 text-xs">
                          <FiMapPin className="w-3.5 h-3.5 text-[#2563EB]" />
                          {event.location}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-24 bg-[#F0F4FF] rounded-3xl border border-blue-100 mt-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <FiCalendar className="w-8 h-8 text-[#2563EB]" />
                </div>
                <p className="text-slate-500 font-semibold text-lg">No events found for this category.</p>
                <p className="text-slate-400 text-sm mt-2">Try selecting a different category above.</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-14">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-10 h-10 flex items-center justify-center bg-[#F0F4FF] text-slate-500 rounded-xl hover:bg-[#2563EB] hover:text-white disabled:opacity-40 disabled:hover:bg-[#F0F4FF] disabled:hover:text-slate-500 transition-all border border-blue-100"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 font-bold text-sm rounded-xl transition-all border ${
                      page === i + 1
                        ? "bg-[#2563EB] text-white border-[#2563EB] shadow-md"
                        : "bg-[#F0F4FF] text-slate-600 border-blue-100 hover:bg-[#2563EB] hover:text-white hover:border-[#2563EB]"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-10 h-10 flex items-center justify-center bg-[#F0F4FF] text-slate-500 rounded-xl hover:bg-[#2563EB] hover:text-white disabled:opacity-40 disabled:hover:bg-[#F0F4FF] disabled:hover:text-slate-500 transition-all border border-blue-100"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NewsEvents;
